/**
 * Hook for infrastructure data import
 *
 * Manages the two-phase import workflow:
 * 1. Validate file - uploads and validates without importing
 * 2. Execute import - imports validated records with duplicate handling
 */

import { useState, useCallback } from 'react'
import type {
  ImportValidationResult,
  ImportExecuteResult,
  DuplicateAction,
} from '../types/infrastructure'
import { runExecute, runValidate } from './use-infrastructure-import/run-actions'

// =============================================================================
// Types
// =============================================================================

export type ImportPhase = 'idle' | 'validating' | 'validated' | 'executing' | 'complete' | 'error'

export interface UseInfrastructureImportResult {
  /** Current phase of the import workflow */
  phase: ImportPhase
  /** Whether an operation is in progress */
  loading: boolean
  /** Error message if any */
  error: string | null
  /** Validation result from the validate phase */
  validationResult: ImportValidationResult | null
  /** Import result from the execute phase */
  importResult: ImportExecuteResult | null
  /** Validate an import file */
  validate: (file: File) => Promise<ImportValidationResult | null>
  /** Execute the import with the validated records */
  execute: (duplicateAction: DuplicateAction) => Promise<ImportExecuteResult | null>
  /** Reset the import state */
  reset: () => void
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to manage infrastructure import workflow
 */
export function useInfrastructureImport(caseId: string | null): UseInfrastructureImportResult {
  const [phase, setPhase] = useState<ImportPhase>('idle')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null)
  const [importResult, setImportResult] = useState<ImportExecuteResult | null>(null)

  const validate = useCallback(
    (file: File) =>
      runValidate({
        caseId,
        file,
        setPhase,
        setLoading,
        setError,
        setValidationResult,
        setImportResult,
      }),
    [caseId]
  )

  const execute = useCallback(
    (duplicateAction: DuplicateAction) =>
      runExecute({
        caseId,
        duplicateAction,
        validationResult,
        setPhase,
        setLoading,
        setError,
        setImportResult,
      }),
    [caseId, validationResult]
  )

  /**
   * Reset the import state
   */
  const reset = useCallback(() => {
    setPhase('idle')
    setLoading(false)
    setError(null)
    setValidationResult(null)
    setImportResult(null)
  }, [])

  return {
    phase,
    loading,
    error,
    validationResult,
    importResult,
    validate,
    execute,
    reset,
  }
}
